/**
 * Demo "what is using this machine" for ?mock=1: the studio runs two heavy commands with two
 * waiting, so it takes no new session (ADR 0063 step 5: more than one waiter is full), Defender
 * has kept 11% of it for a quarter of an hour (the hint shows), and the laptop is busy with a
 * session, its editor and a browser, its work budget free. Built with the demo state, so the slice is
 * there from the first paint.
 */
import { workBudgetFor } from '@orgus/protocol';
import type { AggregateState, LoadGroup, MachineLoadEntry, MachineLoadState, MachineLoadTotals } from '@orgus/protocol';

const S = 1_000;
const MIN = 60 * S;

function totals(groups: readonly LoadGroup[]): MachineLoadTotals {
  const sum = (kinds: readonly string[]) => Math.round(groups.filter((g) => kinds.includes(g.kind)).reduce((n, g) => n + g.pct, 0) * 10) / 10;
  return {
    work: sum(['work']),
    sessions: sum(['session']),
    projects: sum(['project']),
    background: sum(['editor', 'antivirus', 'indexer', 'browser', 'system', 'orgus', 'other']),
    antivirus: sum(['antivirus']),
    shortLived: sum(['short-lived']),
  };
}

export function mockMachineLoad(st: Pick<AggregateState, 'machines' | 'sessions' | 'projects'>, now: number, ids: { studio: string; mbp: string; orgus: string; qawl: string; billing: string }): MachineLoadState {
  const out: MachineLoadEntry[] = [];
  const working = (machineId: string) => st.sessions.filter((s) => s.machineId === machineId && s.status === 'working');
  const studio = st.machines.find((m) => m.id === ids.studio);
  if (studio) {
    const [a, b] = working(studio.id);
    const groups: LoadGroup[] = [
      { kind: 'work', label: 'pnpm test:affected', pct: 17.4, processes: 38, tools: ['vitest', 'tsc', 'turbo'], leaseId: 'lease-test', projectId: ids.orgus },
      { kind: 'antivirus', label: 'Antivirus', pct: 11.2, processes: 1, tools: ['Defender'] },
      { kind: 'editor', label: 'Editors', pct: 5.6, processes: 41, tools: ['VS Code', 'tsserver', 'gopls'] },
      { kind: 'short-lived', label: 'Short-lived processes', pct: 4.1, processes: 0 },
      ...(a ? [{ kind: 'session' as const, label: 'Session', pct: 3.3, processes: 6, tools: ['claude', 'git'], sessionId: a.id, ...(a.projectId ? { projectId: a.projectId } : {}) }] : []),
      { kind: 'system', label: 'Windows and services', pct: 2.4, processes: 22, tools: ['System', 'svchost', 'dwm'] },
      { kind: 'indexer', label: 'Search indexer', pct: 1.1, processes: 2, tools: ['Windows Search'] },
    ];
    out.push({
      machineId: studio.id,
      at: now - 8 * S,
      windowMs: 30_000,
      sampleAt: now - 8 * S,
      load: studio.load,
      cpus: studio.cpus,
      groups,
      totals: totals(groups),
      work: {
        budget: workBudgetFor(studio.cpus),
        used: 12,
        leases: [
          { id: 'lease-test', pid: 31_204, slots: 6, what: 'pnpm test:affected', cwd: 'C:/Users/Srika/CascadeProjects/orgus/.claude/worktrees/cpu-visibility', since: now - 2 * MIN - 40 * S, projectId: ids.orgus, ...(a ? { sessionId: a.id } : {}) },
          { id: 'lease-land', pid: 28_810, slots: 6, what: 'orgus land', cwd: 'C:/src/qawl', since: now - 50 * S, projectId: ids.qawl, ...(b ? { sessionId: b.id } : {}) },
        ],
        waiting: [
          { id: 'wait-tc', pid: 30_112, want: 6, min: 2, what: 'pnpm typecheck', cwd: 'C:/src/billing', since: now - 20 * S, projectId: ids.billing },
          { id: 'wait-land', pid: 30_540, want: 6, min: 2, what: 'orgus land', cwd: 'C:/Users/Srika/CascadeProjects/orgus/.claude/worktrees/admission', since: now - 6 * S, projectId: ids.orgus },
        ],
      },
      antivirusHigh: { since: now - 16 * MIN, avgPct: 11.8 },
    });
  }
  const mbp = st.machines.find((m) => m.id === ids.mbp);
  if (mbp) {
    const [a] = working(mbp.id);
    const groups: LoadGroup[] = [
      ...(a ? [{ kind: 'session' as const, label: 'Session', pct: 7.5, processes: 9, tools: ['claude', 'node'], sessionId: a.id, ...(a.projectId ? { projectId: a.projectId } : {}) }] : []),
      { kind: 'editor', label: 'Editors', pct: 6, processes: 18, tools: ['VS Code', 'tsserver'] },
      { kind: 'browser', label: 'Browsers', pct: 4.1, processes: 12, tools: ['chrome'] },
      { kind: 'project', label: 'qawl', pct: 2.4, processes: 3, tools: ['go test'], projectId: ids.qawl },
      { kind: 'other', label: 'Other apps', pct: 2, processes: 30, tools: ['Slack', 'Spotify'] },
    ];
    out.push({ machineId: mbp.id, at: now - 14 * S, windowMs: 30_000, sampleAt: now - 14 * S, load: mbp.load, cpus: mbp.cpus, groups, totals: totals(groups), work: { budget: workBudgetFor(mbp.cpus), used: 0, leases: [], waiting: [] } });
  }
  return { machines: out };
}
